import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query'
import { Page } from '@progress/kendo-react-dropdowns'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import {
  InterestRateConfigDto,
  InterestRateFilterKey,
  InterestRateKey,
} from 'api/Interest-Rates/InterestRateDto'
import { PageData } from 'api/Pagination/Types'
import AddButton from 'components/AddButton/AddButton'
import { Pager } from 'components/components/Pager'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Modal } from 'components/CustomModal'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { calculatePageIndex, calculateSkipIndex } from 'features/DataGrid/helpers'
import { useAppDispatch, useAppSelector } from 'store'
import { pageableSettings } from 'store/grid/grid.slice'
import { fetchCountriesAction, fetchTickmillCompaniesAction } from 'store/identity/identity.actions'
import { getCurrenciesAction } from 'store/trading-accounts/trading-accounts.actions'
import { getWalletTypesAction } from 'store/wallet/wallet-actions'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOneWithFetchingUpdate } from 'utils/api/useFetchOneWithFetchingUpdate'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery, FilterLogic } from 'utils/QueryHelpers'
import {
  TableStateContext,
  TableStateContextAction,
  TableStateContextData,
} from 'utils/TableStateContext'

import { createFilterNormalizerWithMapping } from '../../../features/DataGrid/helpers/normalizeFilter'
import { AddInterestRateForm, AddInterestRateFormValues } from './AddInterestRateForm'
import {
  AdministrationAllowedInterestRatesNavigation,
  AdministrationAllowedInterestRatesTab,
} from './AdministrationInterestRatesNavigation'
import { EditInterestRateForm, EditInterestRateFormValues } from './EditInterestRateForm'
import interestRatesWalletConfigurationColumns from './InterestRatesWalletsConfigurationColumns'

interface ModalProps {
  data: InterestRateConfigDto | undefined
  visible: boolean
}

const initialModalState = () => ({
  data: undefined,
  visible: false,
})

const useMapStateToProps = () => ({
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
  countries: useAppSelector((state) => state.identity.countries),
  currencies: useAppSelector((state) => state.tradingAccounts.currencies),
  walletTypes: useAppSelector((state) => state.wallet.walletType),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)
  const walletsApiClient = useApiClient(WalletsApiClient)
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  return {
    fetchCountries: async () => dispatch(fetchCountriesAction(identityApiClient)),
    fetchTickmillCompanies: async () => dispatch(fetchTickmillCompaniesAction(identityApiClient)),
    fetchCurrencies: async () => dispatch(getCurrenciesAction(tradingAccountsApiClient)),
    fetchWalletTypes: async () => dispatch(getWalletTypesAction(walletsApiClient)),
  }
}

const useInterestRatesFetch = (): InterestRatesFilterProps => {
  const { fetchCountries, fetchTickmillCompanies, fetchWalletTypes, fetchCurrencies } =
    useMapDispatchToProps()
  const { countries, tickmillCompanies, walletTypes, currencies } = useMapStateToProps()

  const interestRatesFilter = useInterestRatesFilter()

  useEffect(() => {
    if (isEmpty(countries)) {
      fetchCountries()
    }
    if (isEmpty(tickmillCompanies)) {
      fetchTickmillCompanies()
    }
    if (isEmpty(walletTypes)) {
      fetchWalletTypes()
    }
    if (isEmpty(currencies)) {
      fetchCurrencies()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return interestRatesFilter
}

interface InterestRatesFilterProps {
  data: PageData<InterestRateConfigDto[]> | undefined
  tableData: TableStateContextData
  setTableData: TableStateContextAction
  pageIndex: number
  pageSize: number
  isLoading: boolean
  forceRefresh(): void
  filter: CompositeFilterDescriptor
  setFilter(value: CompositeFilterDescriptor): void
  pagination: Page

  setPagination(page: Page | ((x: Page) => Page)): void
  handleFilterChange(event: GridFilterChangeEvent): void
  handlePageChange(event: GridPageChangeEvent): void
}

const useInterestRatesFilter = (): InterestRatesFilterProps => {
  const walletsApiClient = useApiClient(WalletsApiClient)

  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [filter, setFilter] = useState<CompositeFilterDescriptor>({ logic: 'and', filters: [] })
  const [paginationState, setPaginationState] = useState<Page>(getPaginationInitialState())

  const { callback, forceRefresh } = useCallbackWithForceRefresh(
    () => {
      return walletsApiClient.getInterestRatesConfig({
        search: getSearchQuery(),
        pageIndex: calculatePageIndex(paginationState.skip, paginationState.take),
        pageSize: paginationState.take,
        skipCount: tableData?.skipCount,
      })
    },
    [paginationState, tableData?.skipCount] // eslint-disable-line react-hooks/exhaustive-deps
  )
  const { data, isLoading } = useFetchOneWithFetchingUpdate(callback)

  const getSearchQuery = useCallback(
    () => {
      const filterSearchQuery = normalizeSearchQuery(filter)
      return (
        buildQuery({
          logic: FilterLogic.AND,
          filters: filterSearchQuery.filters,
        }) ?? null
      )
    },
    [filter.filters] // eslint-disable-line react-hooks/exhaustive-deps
  )

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!isLoading) {
        setPaginationState((page) => ({ ...page, skip: 0 }))
      }
    }, 500)
    return () => clearTimeout(timeoutId)
  }, [filter.filters]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setTableData((props) => {
      return { ...props, skipCount: tableData?.skipCount, meta: data }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.pageIndex, data?.pageSize, data?.totalResults, tableData?.skipCount])

  const handleFilterChange = (event: GridFilterChangeEvent) => {
    setFilter(event.filter)
  }

  const handlePageChange = (event: GridPageChangeEvent) => {
    setPaginationState(event.page)
  }

  return {
    data,
    tableData,
    setTableData,
    pageIndex: calculatePageIndex(paginationState.skip, paginationState.take),
    pageSize: paginationState.take ?? 10,
    isLoading,
    forceRefresh,
    filter,
    setFilter,
    pagination: paginationState,
    setPagination: setPaginationState,
    handleFilterChange,
    handlePageChange,
  }
}

const useTablePager = (props: InterestRatesFilterProps) => {
  const { data, pagination, setPagination, tableData, setTableData } = props

  useEffect(() => {
    setTableData((state) => {
      return { ...state, skipCount: tableData?.skipCount, meta: data }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.pageIndex, data?.pageSize, data?.totalResults, tableData?.skipCount])

  const handlePageChangePager = (pagerIndex: number) => {
    setPagination((state) => {
      return { ...state, skip: calculateSkipIndex(pagerIndex, pagination.take) }
    })
  }

  const handleSizeChangePager = (take: number) => {
    setPagination((state) => {
      return { ...state, skip: 0, take }
    })
  }

  const handleSkipCountChangePager = (skipCount: boolean) => {
    setTableData((state) => {
      return { ...state, skipCount }
    })
  }

  return { handlePageChangePager, handleSizeChangePager, handleSkipCountChangePager }
}

const InterestRatesWalletsConfigurationPage = (): JSX.Element => {
  const walletsApiClient = useApiClient(WalletsApiClient)
  const setToast = useContext(ToastContext)
  const [auth] = useContext(AuthSessionContext)

  const { countries, tickmillCompanies, walletTypes, currencies } = useMapStateToProps()
  const interestRates = useInterestRatesFetch()
  const tablePager = useTablePager(interestRates)

  const { handlePageChangePager, handleSizeChangePager, handleSkipCountChangePager } = tablePager
  const { data, isLoading, filter, pagination, tableData, pageIndex, pageSize } = interestRates
  const { forceRefresh, handlePageChange, handleFilterChange, setTableData } = interestRates

  const [addModalOpen, setAddModalOpen] = useState(false)
  const [editModal, setEditModal] = useState<ModalProps>(initialModalState())
  const [deleteInterestRateModal, setDeleteInterestRateModal] =
    useState<ModalProps>(initialModalState())

  const permissions = auth?.permissions || []

  const handleAddInterestRate = async (values: AddInterestRateFormValues) => {
    try {
      await walletsApiClient.addInterestRatesConfig(values)
      setToast(successToast('Interest rate has been added.'))
      forceRefresh()
    } catch (error) {
      console.error(error)
    } finally {
      setAddModalOpen(false)
    }
  }

  const handleEditInterestRate = async (values: EditInterestRateFormValues) => {
    try {
      await walletsApiClient.updateInterestRatesConfig(values)
      setToast(successToast('Interest rate has been updated.'))
      forceRefresh()
    } catch (error) {
      console.error(error)
    } finally {
      setEditModal(initialModalState())
    }
  }

  const handleConfirmationDelete = async () => {
    if (deleteInterestRateModal.data) {
      try {
        await walletsApiClient.deleteInterestRatesConfig(deleteInterestRateModal.data.id)
        setToast(successToast('Interest rate has been deleted.'))
        handleEditCancel()
        handleConfirmationCancel()
      } catch (error) {
        console.error(error)
        handleEditCancel()
        handleConfirmationCancel()
      } finally {
        forceRefresh()
      }
    }
  }

  const handleEditCancel = () => {
    setEditModal(initialModalState())
  }

  const handleAddCancel = () => {
    setAddModalOpen(false)
  }

  const handleConfirmationCancel = () => {
    setDeleteInterestRateModal(initialModalState())
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <>
      <Breadcrumbs
        crumbs={[
          {
            title: 'Interest Rates Config',
            link: links.administrationInterestRateConfigControls.url,
          },
        ]}
      />
      <AdministrationAllowedInterestRatesNavigation
        tab={AdministrationAllowedInterestRatesTab.WALLETS_CONFIGURATION}
      />
      <AddButton
        disabled={!hasPermission(permissions, PermissionsEnum.CanEditInterestRatesConfig)}
        text='Add new configuration'
        onClick={() => setAddModalOpen(true)}
        data-test='add-new-configuration'
      />
      {deleteInterestRateModal.data && (
        <ConfirmationModal
          yesButtonText='Yes, delete'
          onYes={handleConfirmationDelete}
          onCancel={handleConfirmationCancel}
          visible={deleteInterestRateModal.visible}
          renderTitle={<ConfirmationDeleteInterestRateModal {...deleteInterestRateModal} />}
        />
      )}
      {addModalOpen && (
        <Modal visible={addModalOpen} onClose={handleAddCancel}>
          <AddInterestRateForm
            permissions={permissions}
            tickmillCompanies={tickmillCompanies}
            countries={countries}
            currencies={currencies}
            walletTypes={walletTypes}
            onSubmit={handleAddInterestRate}
          />
        </Modal>
      )}
      {editModal.visible && (
        <Modal visible={editModal.visible} onClose={handleEditCancel}>
          <EditInterestRateForm
            data={editModal.data as InterestRateConfigDto}
            permissions={permissions}
            tickmillCompanies={tickmillCompanies}
            countries={countries}
            currencies={currencies}
            walletTypes={walletTypes}
            onSubmit={handleEditInterestRate}
          />
        </Modal>
      )}
      {data && (
        <TableStateContext.Provider value={[tableData, setTableData]}>
          <Grid
            filterable
            resizable
            data={data.items}
            filter={filter}
            take={pagination.take}
            skip={pagination.skip}
            total={data.totalResults}
            onPageChange={handlePageChange}
            onFilterChange={handleFilterChange}
            pageable={pageableSettings}
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChangePager}
                onPageChange={handlePageChangePager}
                onPageSizeChange={handleSizeChangePager}
                page={pageIndex}
                pageSize={pageSize}
                take={pagination.take || 10}
                skip={pagination.skip || 0}
              />
            )}
            style={{ zIndex: 0 }}
          >
            {interestRatesWalletConfigurationColumns
              .filter((c) => c.show !== false)
              .map((column, idx) => (
                <Column key={idx} {...column} />
              ))}
            {hasPermission(permissions, PermissionsEnum.CanEditInterestRatesConfig) && (
              <Column
                title=''
                cell={(props: GridCellProps) => (
                  <td>
                    <MoreButton
                      items={[
                        {
                          name: 'Edit',
                          'data-test': 'edit',
                          disabled: !hasPermission(
                            permissions,
                            PermissionsEnum.CanEditInterestRatesConfig
                          ),
                          onClick: () =>
                            setEditModal({
                              visible: true,
                              data: props.dataItem,
                            }),
                        },
                        {
                          name: 'Remove',
                          'data-test': 'remove',
                          disabled: !hasPermission(
                            permissions,
                            PermissionsEnum.CanEditInterestRatesConfig
                          ),
                          onClick: () =>
                            setDeleteInterestRateModal({
                              visible: true,
                              data: props.dataItem,
                            }),
                        },
                      ]}
                      data-test='more-button'
                    />
                  </td>
                )}
                filterable={false}
                width={50}
              />
            )}
          </Grid>
        </TableStateContext.Provider>
      )}
    </>
  )
}

const ConfirmationDeleteInterestRateModal: React.FC<ModalProps> = (props) => {
  const { data } = props

  return (
    <div>
      <p>
        Are you sure you want to
        <br />
        delete the following interest rate record?
      </p>

      <div>
        <div id='field'>
          <strong>Tickmill company</strong> {data?.tickmillCompany.name}
        </div>
        <div id='field'>
          <strong>Country</strong> {data?.country.name}
        </div>
        <div id='field'>
          <strong>Currency</strong> {data?.currency.name}
        </div>
      </div>
    </div>
  )
}

const normalizeSearchQuery = (filterDescriptor: CompositeFilterDescriptor) => {
  const fieldMappings: { [key: string]: InterestRateFilterKey } = {
    [InterestRateKey.tickmillCompanyName]: InterestRateFilterKey.TICKMILL_COMPANY_ID,
    [InterestRateKey.country]: InterestRateFilterKey.COUNTRY,
    [InterestRateKey.currency]: InterestRateFilterKey.CURRENCY,
    [InterestRateKey.walletType]: InterestRateFilterKey.WALLET_TYPE_ID,
    [InterestRateKey.enabled]: InterestRateFilterKey.ENABLED,
  }

  const normalizeFilters = createFilterNormalizerWithMapping(fieldMappings)
  const filters = normalizeFilters(filterDescriptor)

  return { filters }
}

export default InterestRatesWalletsConfigurationPage
